import React from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

function getRiskConfig(level: number): { color: string; label: string; arcColor: string } {
  if (level <= 1) return { color: '#22c55e', label: 'Low Risk', arcColor: '#22c55e' };
  if (level <= 2) return { color: '#f59e0b', label: 'Medium Risk', arcColor: '#f59e0b' };
  return { color: '#ef4444', label: 'High Risk', arcColor: '#ef4444' };
}

const GovernanceRadarWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const governanceData = state?.governanceData ?? null;

  if (!state || !governanceData) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No governance data
      </div>
    );
  }

  const riskLevel = governanceData.riskClassification ?? 1;
  const config = getRiskConfig(riskLevel);

  // SVG arc gauge — similar to engagement gauge
  const radius = 55;
  const strokeWidth = 10;
  const cx = 70;
  const cy = 65;
  const startAngle = Math.PI;
  const scoreAngle = startAngle - (riskLevel / 3) * Math.PI;

  const arcPath = (angle: number) => {
    const x = cx + radius * Math.cos(angle);
    const y = cy - radius * Math.sin(angle);
    return `${x} ${y}`;
  };

  const bgArc = `M ${arcPath(startAngle)} A ${radius} ${radius} 0 1 1 ${arcPath(0)}`;
  const valueArc = `M ${arcPath(startAngle)} A ${radius} ${radius} 0 ${riskLevel > 1.5 ? 1 : 0} 1 ${arcPath(scoreAngle)}`;

  // Survey progress (if available)
  const totalQuestions = governanceData.surveyQuestions?.length ?? 0;
  const answeredQuestions = governanceData.surveyQuestions?.filter((q: any) => q.answer)?.length ?? 0;

  return (
    <div className="flex flex-col items-center justify-center h-full px-4">
      <svg viewBox="0 0 140 80" className="w-full max-w-[180px]">
        {/* Background arc */}
        <path
          d={bgArc}
          fill="none"
          stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Risk arc */}
        <path
          d={valueArc}
          fill="none"
          stroke={config.arcColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${config.arcColor}50)`, transition: 'all 0.8s ease-out' }}
        />
        {/* Level text */}
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          className={`font-bold ${isDark ? 'fill-white' : 'fill-slate-900'}`}
          style={{ fontSize: '24px', fontFamily: 'ui-monospace, monospace' }}
        >
          {riskLevel}
        </text>
        <text
          x={cx}
          y={cy + 8}
          textAnchor="middle"
          className={isDark ? 'fill-white/30' : 'fill-slate-400'}
          style={{ fontSize: '8px' }}
        >
          / 3
        </text>
      </svg>

      <span
        className="text-xs font-semibold uppercase tracking-wider mt-1"
        style={{ color: config.color }}
      >
        {config.label}
      </span>

      {totalQuestions > 0 && (
        <div className="flex items-center gap-2 mt-2">
          <div className={`h-1.5 w-16 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${(answeredQuestions / totalQuestions) * 100}%` }}
            />
          </div>
          <span className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
            {answeredQuestions}/{totalQuestions}
          </span>
        </div>
      )}
    </div>
  );
};

export default GovernanceRadarWidget;
